// src/modules/auth/services/authService.ts

import { httpClient } from '../../../shared/services/httpClient';
import { API_ENDPOINTS, AUTH_CONFIG } from '../../../shared/services/apiConfig';
import { ApiResponse } from '../../../types/api';
import { UserData, LoginResponse } from '../types';

/**
 * Perfil completo del usuario (incluye relaciones con otras tablas)
 * Este es el tipo que retorna GET /v1/users/profile
 */
export interface UserProfile extends UserData {
  bio?: string;
  phone?: string;
  country?: string;
  roles?: Array<{
    role: 'participant' | 'speaker' | 'judge';
    is_active: boolean;
    created_at: string;
  }>;
  participations?: Array<{
    id: number;
    event_id: number;
    role: 'participant' | 'speaker' | 'judge';
    status: string;
    registration_date: string;
    title?: string;
    start_date?: string;
    end_date?: string;
    event_status?: string;
  }>;
  certificates?: Array<{
    id: number;
    event_id: number;
    type: string;
    title: string;
    issued_date: string;
    event_title?: string;
  }>;
}

/**
 * Servicio de autenticación
 * Maneja login con Google, tokens y sesión del usuario
 */
class AuthService {
  /**
   * Login con Google usando el token de credenciales
   * Endpoint: POST /v1/auth/google
   * Body: { token: string }
   */
  async loginWithGoogle(googleToken: string): Promise<ApiResponse<LoginResponse['data']>> {
    try {
      console.log('🔐 Iniciando login con Google...');
      console.log('📤 Token:', googleToken.substring(0, 50) + '...');

      const response = await httpClient.post<LoginResponse['data']>(
        API_ENDPOINTS.AUTH.GOOGLE_LOGIN,
        { token: googleToken }
      );

      console.log('📥 Respuesta del servidor:', response);

      // Si el login fue exitoso, guardar datos
      if (response.success && response.data) {
        this.saveAuthData(response.data.user, response.data.token);
        console.log('✅ Autenticación exitosa:', response.data.user.email);
      } else {
        console.error('❌ Login fallido:', response.message);
      }

      return response;

    } catch (error: any) {
      console.error('❌ Error en loginWithGoogle:', error);
      return {
        success: false,
        message: error.message || 'Error en autenticación con Google',
        data: null,
      };
    }
  }

  /**
   * Obtener perfil completo del usuario actual
   * Endpoint: GET /v1/users/profile
   * Requiere autenticación (token JWT)
   * Retorna: UserData + roles + participations + certificates
   */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    try {
      console.log('👤 Obteniendo perfil del usuario...');

      const response = await httpClient.get<UserProfile>(
        API_ENDPOINTS.USERS.PROFILE
      );

      if (response.success && response.data) {
        // Actualizar solo los datos básicos del usuario (no roles/participations)
        const basicUserData: UserData = {
          id: response.data.id,
          email: response.data.email,
          name: response.data.name,
          picture: response.data.picture,
          google_id: response.data.google_id,
          is_active: response.data.is_active,
          created_at: response.data.created_at,
          last_login: response.data.last_login,
        };
        this.updateUserData(basicUserData);
      }

      return response;

    } catch (error: any) {
      console.error('❌ Error obteniendo perfil:', error);
      return {
        success: false,
        message: error.message || 'Error obteniendo perfil',
        data: null,
      };
    }
  }

  /**
   * Actualizar perfil del usuario
   * Endpoint: PUT /v1/users/profile
   */
  async updateProfile(data: Partial<UserData>): Promise<ApiResponse<UserData>> {
    try {
      console.log('📝 Actualizando perfil...', data);

      const response = await httpClient.put<UserData>(
        API_ENDPOINTS.USERS.PROFILE,
        data
      );

      if (response.success && response.data) {
        this.updateUserData(response.data);
      }

      return response;

    } catch (error: any) {
      console.error('❌ Error actualizando perfil:', error);
      return {
        success: false,
        message: error.message || 'Error actualizando perfil',
        data: null,
      };
    }
  }

  /**
   * Cerrar sesión
   */
  async logout(): Promise<ApiResponse<void>> {
    try {
      console.log('🚪 Cerrando sesión...');
      
      // Limpiar datos locales
      this.clearAuthData();
      
      // Disparar evento de logout
      window.dispatchEvent(new CustomEvent('auth:logout'));

      return {
        success: true,
        message: 'Sesión cerrada correctamente',
        data: null,
      };

    } catch (error: any) {
      // Asegurar limpieza incluso si hay error
      this.clearAuthData();
      
      return {
        success: true,
        message: 'Sesión cerrada',
        data: null,
      };
    }
  }

  /**
   * Obtener usuario actual del localStorage
   */
  getCurrentUser(): UserData | null {
    try {
      const userData = localStorage.getItem(AUTH_CONFIG.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error leyendo usuario del localStorage:', error);
      return null;
    }
  }

  /**
   * Obtener token JWT actual
   */
  getToken(): string | null {
    return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  }

  /**
   * Verificar si hay una sesión activa
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    
    if (!token || !user) {
      return false;
    }

    // Verificar si el token no ha expirado
    try {
      const payload = this.decodeToken(token);
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  /**
   * Decodificar token JWT (sin verificar firma)
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decodificando token:', error);
      throw error;
    }
  }

  /**
   * Guardar datos de autenticación en localStorage
   */
  private saveAuthData(user: UserData, token: string, refreshToken?: string): void {
    console.log('💾 Guardando datos de autenticación:', {
      email: user.email,
      hasToken: !!token,
    });

    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
    localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));

    if (refreshToken) {
      localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken);
    }

    // Disparar evento de login
    window.dispatchEvent(new CustomEvent('auth:login', { detail: { user } }));
  }

  /**
   * Actualizar solo los datos del usuario
   */
  private updateUserData(user: UserData): void {
    localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
    window.dispatchEvent(new CustomEvent('auth:user-updated', { detail: { user } }));
  }

  /**
   * Limpiar todos los datos de autenticación
   */
  private clearAuthData(): void {
    console.log('🗑️ Limpiando datos de autenticación');
    
    localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  }

  /**
   * Verificar si el token está por expirar pronto
   */
  isTokenExpiringSoon(): boolean {
    try {
      const token = this.getToken();
      if (!token) return true;

      const payload = this.decodeToken(token);
      const now = Date.now();
      const expiryTime = payload.exp * 1000;
      
      // Retornar true si expira en los próximos 5 minutos
      return (expiryTime - now) < AUTH_CONFIG.TOKEN_EXPIRY_BUFFER;
    } catch {
      return true;
    }
  }
}

export const authService = new AuthService();